import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { DeveloperNav } from '@/components/DeveloperNav';
import Navigation from '@/components/Navigation';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Code, 
  Key, 
  BarChart3, 
  Settings, 
  HelpCircle, 
  CheckCircle, 
  XCircle, 
  Clock,
  ExternalLink,
  Copy,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Zap,
  Shield,
  Book,
  ArrowRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface DeveloperAccount {
  id: number;
  companyName?: string;
  website?: string;
  description?: string;
  useCase?: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  tier: 'free' | 'pro' | 'enterprise';
  monthlyLimit: number;
  createdAt: string;
}

interface ApiKey {
  id: number;
  keyName: string;
  keyPrefix: string;
  permissions: Record<string, boolean>;
  isActive: boolean;
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
}

interface UsageStats {
  totalCalls: number;
  successfulCalls: number;
  errorCalls: number;
  averageResponseTime: number;
  totalRequestSize: number;
  totalResponseSize: number;
  dailyUsage: Record<string, number>;
}

interface ApiPlan {
  id: number;
  name: string;
  description: string;
  tier: string;
  monthlyLimit: number;
  price: number;
  features: string[];
}

export default function DeveloperPortal() {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<Record<string, boolean>>({});
  const [newKeyExpiresAt, setNewKeyExpiresAt] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<number | null>(null);

  // TanStack Query for developer account
  const { data: developerAccountData, isLoading: accountLoading, error: accountError } = useQuery<{ success: boolean; data: DeveloperAccount }>({
    queryKey: ['/api/developer/account'],
    enabled: isAuthenticated,
  });
  const developerAccount = developerAccountData?.data;

  // TanStack Query for API keys
  const { data: apiKeysData, isLoading: keysLoading } = useQuery<{ success: boolean; data: ApiKey[] }>({
    queryKey: ['/api/developer/api-keys'],
    enabled: isAuthenticated,
  });
  const apiKeys: ApiKey[] = apiKeysData?.data || [];

  // TanStack Query for usage stats
  const { data: usageStatsData, isLoading: statsLoading } = useQuery<{ success: boolean; data: { usageStats: UsageStats } }>({
    queryKey: ['/api/developer/dashboard'],
    enabled: isAuthenticated,
  });
  const usageStats: UsageStats | null = usageStatsData?.data?.usageStats || null;

  // TanStack Query for API plans (public)
  const { data: apiPlansData, isLoading: plansLoading } = useQuery<{ success: boolean; data: ApiPlan[] }>({
    queryKey: ['/api/developer/plans'],
  });
  const apiPlans: ApiPlan[] = apiPlansData?.data || [];

  const loading = accountLoading || keysLoading || statsLoading || plansLoading;

  // Redirect to developer dashboard if user has a developer account
  useEffect(() => {
    if (!accountLoading && developerAccount) {
      navigate('/developer/dashboard');
    }
  }, [accountLoading, developerAccount, navigate]);

  // Mutation for creating API key
  const createApiKeyMutation = useMutation({
    mutationFn: async (data: { keyName: string; permissions: Record<string, boolean>; expiresAt?: string }) => {
      const response = await apiRequest('POST', '/api/developer/api-keys', data);
      return response.json();
    },
    onSuccess: (data) => {
      setCreatedApiKey(data.data.apiKey);
      setShowApiKey(true);
      setNewKeyName('');
      setNewKeyPermissions({});
      setNewKeyExpiresAt('');
      setShowCreateKey(false);
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'Success',
        description: 'API key created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create API key',
        variant: 'destructive',
      });
    },
  });

  const createApiKey = () => {
    createApiKeyMutation.mutate({
      keyName: newKeyName,
      permissions: newKeyPermissions,
      expiresAt: newKeyExpiresAt || undefined,
    });
  };

  // Mutation for toggling API key status
  const toggleApiKeyMutation = useMutation({
    mutationFn: async ({ keyId, isActive }: { keyId: number; isActive: boolean }) => {
      const response = await apiRequest('PUT', `/api/developer/api-keys/${keyId}`, { isActive });
      return response.json();
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'Success',
        description: `API key ${isActive ? 'activated' : 'deactivated'}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: 'Failed to update API key',
        variant: 'destructive',
      });
    },
  });

  const toggleApiKeyStatus = (keyId: number, isActive: boolean) => {
    toggleApiKeyMutation.mutate({ keyId, isActive });
  };

  // Mutation for deleting API key
  const deleteApiKeyMutation = useMutation({
    mutationFn: async (keyId: number) => {
      const response = await apiRequest('DELETE', `/api/developer/api-keys/${keyId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'Success',
        description: 'API key deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete API key',
        variant: 'destructive',
      });
    },
  });

  const deleteApiKey = (keyId: number) => {
    setKeyToDelete(keyId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteApiKey = () => {
    if (keyToDelete) {
      deleteApiKeyMutation.mutate(keyToDelete);
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'API key copied to clipboard',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'suspended':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navigation />
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-gray-900">Developer Portal</CardTitle>
              <CardDescription>
                Please log in to access the developer portal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <a href="/auth">Log In</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navigation />
        <div className="flex items-center justify-center p-4 pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading developer portal...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!developerAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navigation />
        <div className="pt-20 p-4">
          <div className="max-w-6xl mx-auto">
            {/* API Marketing Section */}
            <section className="py-12 sm:py-20 px-4 bg-white rounded-2xl shadow-lg mb-12">
              <div className="container mx-auto max-w-6xl">
                <div className="text-center mb-12">
                  <Badge className="mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm">
                    <Code className="w-4 h-4 mr-2" />
                    For Developers
                  </Badge>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                    Build Amazing Apps with Our API
                  </h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Integrate AI-powered story generation into your applications. 
                    Create personalized content, educational tools, and interactive experiences for children.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Zap className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
                      <p className="text-gray-600">
                        Generate stories in seconds with our optimized AI models
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Secure & Reliable</h3>
                      <p className="text-gray-600">
                        Enterprise-grade security with 99.9% uptime guarantee
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Book className="w-8 h-8 text-purple-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Comprehensive Docs</h3>
                      <p className="text-gray-600">
                        Detailed documentation, SDKs, and interactive API explorer
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="text-center">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link href="/developer-signup">
                      <Button size="lg" variant="gradient" className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto">
                        <Code className="mr-2 h-5 w-5" />
                        Get API Access
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                    <Link href="/api-docs">
                      <Button size="lg" variant="outline" className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                        <Book className="mr-2 h-5 w-5" />
                        View Documentation
                      </Button>
                    </Link>
                  </div>
                  <p className="text-gray-500 mt-4 text-sm">
                    Subscription required • Comprehensive API access • Start building today
                  </p>
                </div>
              </div>
            </section>

            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Developer Account Setup</h1>
              <p className="text-xl text-gray-600">
                Create your developer account to get started with our API
              </p>
            </div>

            <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Create Developer Account</CardTitle>
              <CardDescription>
                Get started with our API by creating a developer account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyName">Company Name (Optional)</Label>
                  <Input
                    id="companyName"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    id="website"
                    placeholder="https://yourcompany.com"
                    type="url"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Tell us about your project..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="useCase">How will you use our API?</Label>
                <Textarea
                  id="useCase"
                  placeholder="Describe your use case..."
                  rows={3}
                />
              </div>
              <Button className="w-full">
                Create Developer Account
              </Button>
            </CardContent>
          </Card>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {apiPlans.map((plan) => (
              <Card key={plan.id} className="text-center">
                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-blue-600">
                    ${plan.price}/month
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>{plan.monthlyLimit.toLocaleString()} API calls/month</li>
                    {plan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <DeveloperNav />
      <div className="p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 leading-tight">Developer Portal</h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
            Manage your API access and monitor usage
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="touch-manipulation">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Account Status</p>
                  <div className="mt-1">
                    {getStatusBadge(developerAccount.status)}
                  </div>
                </div>
                <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="touch-manipulation">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">API Keys</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{apiKeys.length}</p>
                </div>
                <Key className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="touch-manipulation">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Monthly Calls</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">
                    {usageStats?.totalCalls || 0}
                  </p>
                </div>
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="touch-manipulation">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Plan</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">
                    {developerAccount.tier}
                  </p>
                </div>
                <Code className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="keys" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="grid w-full grid-cols-4 min-w-[400px] h-12">
              <TabsTrigger value="keys" className="text-xs sm:text-sm h-10" data-testid="tab-api-keys">API Keys</TabsTrigger>
              <TabsTrigger value="usage" className="text-xs sm:text-sm h-10" data-testid="tab-usage">Usage</TabsTrigger>
              <TabsTrigger value="docs" className="text-xs sm:text-sm h-10" data-testid="tab-docs">Documentation</TabsTrigger>
              <TabsTrigger value="support" className="text-xs sm:text-sm h-10" data-testid="tab-support">Support</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="keys" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg sm:text-xl">API Keys</CardTitle>
                    <CardDescription className="text-sm sm:text-base leading-relaxed">
                      Manage your API keys for accessing our services
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowCreateKey(true)}
                    className="w-full sm:w-auto h-10 text-sm font-medium flex-shrink-0"
                    data-testid="button-create-key"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Key
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8">
                    <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm sm:text-base">No API keys created yet</p>
                    <Button 
                      className="mt-4 h-10 text-sm font-medium" 
                      onClick={() => setShowCreateKey(true)}
                      data-testid="button-create-first-key"
                    >
                      Create Your First API Key
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div key={key.id} className="border rounded-lg p-4 bg-gray-50 sm:bg-white">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                              <h3 className="font-medium text-sm sm:text-base truncate">{key.keyName}</h3>
                              <Badge variant={key.isActive ? "default" : "secondary"} className="self-start">
                                {key.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 font-mono break-all">
                              {key.keyPrefix}••••••••
                            </p>
                            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                              Created: {new Date(key.createdAt).toLocaleDateString()}
                              {key.lastUsedAt && (
                                <span className="block sm:inline sm:ml-2">
                                  Last used: {new Date(key.lastUsedAt).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 w-10 p-0"
                              onClick={() => toggleApiKeyStatus(key.id, !key.isActive)}
                              data-testid={`button-toggle-key-${key.id}`}
                            >
                              {key.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 w-10 p-0 text-red-600 hover:text-red-700"
                              onClick={() => deleteApiKey(key.id)}
                              data-testid={`button-delete-key-${key.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="usage" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl">Usage Statistics</CardTitle>
                <CardDescription className="text-sm sm:text-base leading-relaxed">
                  Monitor your API usage and performance
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {usageStats ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-lg touch-manipulation">
                      <p className="text-lg sm:text-2xl font-bold text-blue-600">
                        {usageStats.totalCalls}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">Total Calls</p>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-green-50 rounded-lg touch-manipulation">
                      <p className="text-lg sm:text-2xl font-bold text-green-600">
                        {usageStats.successfulCalls}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">Successful</p>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-red-50 rounded-lg touch-manipulation">
                      <p className="text-lg sm:text-2xl font-bold text-red-600">
                        {usageStats.errorCalls}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">Errors</p>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-yellow-50 rounded-lg touch-manipulation">
                      <p className="text-lg sm:text-2xl font-bold text-yellow-600">
                        {Math.round(usageStats.averageResponseTime)}ms
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">Avg Response</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm sm:text-base text-center py-4">No usage data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="docs" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl">API Documentation</CardTitle>
                <CardDescription className="text-sm sm:text-base leading-relaxed">
                  Learn how to integrate with our API
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                  <Alert>
                    <Code className="h-4 w-4" />
                    <AlertDescription className="text-sm sm:text-base leading-relaxed">
                      API documentation is coming soon. For now, you can explore our endpoints using the API keys above.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm sm:text-base">Base URL</h3>
                    <code className="block p-3 bg-gray-100 rounded text-xs sm:text-sm font-mono break-all">
                      https://snugglecrafters.com/api
                    </code>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm sm:text-base">Authentication</h3>
                    <code className="block p-3 bg-gray-100 rounded text-xs sm:text-sm font-mono break-all">
                      Authorization: Bearer YOUR_API_KEY
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Support</CardTitle>
                <CardDescription>
                  Get help with your API integration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <HelpCircle className="h-8 w-8 text-blue-600" />
                    <div>
                      <h3 className="font-medium">Need Help?</h3>
                      <p className="text-sm text-gray-600">
                        Contact our support team for assistance
                      </p>
                    </div>
                  </div>
                  
                  <Button asChild>
                    <a href="mailto:developers@snugglecrafters.com">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Contact Support
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create API Key Modal */}
        {showCreateKey && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Create API Key</CardTitle>
                <CardDescription>
                  Create a new API key for your application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="My App API Key"
                  />
                </div>
                
                <div>
                  <Label htmlFor="expiresAt">Expires At (Optional)</Label>
                  <Input
                    id="expiresAt"
                    type="datetime-local"
                    value={newKeyExpiresAt}
                    onChange={(e) => setNewKeyExpiresAt(e.target.value)}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={createApiKey} disabled={!newKeyName}>
                    Create Key
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateKey(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Show Created API Key */}
        {showApiKey && createdApiKey && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>API Key Created</CardTitle>
                <CardDescription>
                  Store this key securely - it won't be shown again
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <code className="text-sm break-all">{createdApiKey}</code>
                </div>
                <Button 
                  onClick={() => copyToClipboard(createdApiKey)}
                  className="w-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowApiKey(false);
                    setCreatedApiKey(null);
                  }}
                  className="w-full"
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="dialog-delete-confirmation">
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this API key? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="button-cancel-delete"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteApiKey}
              disabled={deleteApiKeyMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteApiKeyMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}